import { QueryClient, QueryFunction } from "@tanstack/react-query";

const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";

// ─── Token store ─────────────────────────────────────────────────────────────
// Kept in module scope so both apiRequest and the default queryFn can read it.
// Never written to localStorage or sessionStorage — sessionStorage is handled
// by UserProvider, which calls setActiveToken() after restoring/issuing one.

let _activeToken: string | null = null;

export function setActiveToken(tok: string | null) {
  _activeToken = tok;
}

export function getActiveToken(): string | null {
  return _activeToken;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (_activeToken) headers["Authorization"] = `Bearer ${_activeToken}`;
  return headers;
}

// ─── Global 401 handler ──────────────────────────────────────────────────────
// UserProvider registers a callback here so that any 401 response (from a
// stale tab after a server restart / deploy) automatically signs the user out
// and shows a friendly toast rather than silently failing.

type UnauthCallback = () => void;
let _onUnauthenticated: UnauthCallback | null = null;

export function registerUnauthHandler(cb: UnauthCallback) {
  _onUnauthenticated = cb;
}

export function unregisterUnauthHandler() {
  _onUnauthenticated = null;
}

// ─── Error handling ───────────────────────────────────────────────────────────

type ErrorPayload = { error?: string; message?: string };

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function getErrorMessage(res: Response): Promise<string> {
  const fallback = `${res.status}: ${res.statusText}`;
  const text = await res.text();
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text) as ErrorPayload;
    return parsed.error || parsed.message || `${res.status}: ${text}`;
  } catch {
    return `${res.status}: ${text}`;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Global 401 handler — stale session after server restart / new deploy
    if (res.status === 401 && _onUnauthenticated) {
      _onUnauthenticated();
    }
    throw new ApiError(res.status, await getErrorMessage(res));
  }
}

export async function parseJsonResponse<T = any>(res: Response): Promise<T> {
  await throwIfResNotOk(res);
  return (await res.json()) as T;
}

// ─── Core request helpers ─────────────────────────────────────────────────────

/**
 * apiRequest — always attaches the active Bearer token.
 * Pass an explicit `tokenOverride` for pre-login calls (e.g. /api/auth/me
 * during hydration before the module-level token is set).
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown,
  tokenOverride?: string,
): Promise<Response> {
  const tok = tokenOverride ?? _activeToken;
  const headers: Record<string, string> = {};
  if (tok) headers["Authorization"] = `Bearer ${tok}`;
  if (data) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_BASE}${url}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

// ─── TanStack Query defaults ──────────────────────────────────────────────────

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn = <T>(options: {
  on401: UnauthorizedBehavior;
}): QueryFunction<T> =>
  async ({ queryKey }) => {
    const { on401: unauthorizedBehavior } = options;
    const headers: Record<string, string> = authHeaders();
    const res = await fetch(`${API_BASE}${queryKey[0]}`, { headers });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null as T;
    }

    return await parseJsonResponse<T>(res);
  };

// Retry predicate — retries on network errors and 5xx, never on 4xx.
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return true;
}

const retryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 10_000);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30 * 1000,
      retry: shouldRetry,
      retryDelay,
    },
    mutations: {
      retry: shouldRetry,
      retryDelay,
    },
  },
});

// Refetch all active queries when network reconnects after going offline.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    queryClient.invalidateQueries();
  });
}
